import gsap from 'gsap';

const AUTOPLAY_DELAY = 4000;
const SWIPE_THRESHOLD = 40;

export default class UgcSlider extends HTMLElement {
  private slidesContainer: HTMLUListElement | null = null;
  private slides: HTMLLIElement[] = [];
  private navButtons: HTMLButtonElement[] = [];
  private navDots: HTMLSpanElement[] = [];

  private currentIndex = 0;
  private autoplayTimerId: ReturnType<typeof setTimeout> | null = null;
  private touchStartX: number | null = null;
  private slideChangeTl: gsap.core.Timeline | null = null;

  private onNavClickBound = this.onNavClick.bind(this);
  private onTouchStartBound = this.onTouchStart.bind(this);
  private onTouchEndBound = this.onTouchEnd.bind(this);
  private onResizeBound = this.onResize.bind(this);
  private onAutoplayTickBound = this.onAutoplayTick.bind(this);

  connectedCallback(): void {
    this.slidesContainer = this.querySelector('[data-ugc-slider-ref="slides"]');
    this.slides = Array.from(this.querySelectorAll('[data-ugc-slider-ref="slide"]'));
    this.navButtons = Array.from(this.querySelectorAll('[data-ugc-slider-ref="nav-button"]'));
    this.navDots = Array.from(this.querySelectorAll('[data-ugc-slider-ref="nav-dot"]'));

    if (this.slides.length === 0) return;

    this.addEventListeners();

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  addEventListeners(): void {
    this.navButtons.forEach((button) => {
      button.addEventListener('click', this.onNavClickBound);
    });

    if (this.slidesContainer) {
      this.slidesContainer.addEventListener('touchstart', this.onTouchStartBound, { passive: true });
      this.slidesContainer.addEventListener('touchend', this.onTouchEndBound, { passive: true });
    }
    window.addEventListener('resize', this.onResizeBound);
  }

  removeEventListeners(): void {
    this.navButtons.forEach((button) => {
      button.removeEventListener('click', this.onNavClickBound);
    });

    if (this.slidesContainer) {
      this.slidesContainer.removeEventListener('touchstart', this.onTouchStartBound);
      this.slidesContainer.removeEventListener('touchend', this.onTouchEndBound);
    }
    window.removeEventListener('resize', this.onResizeBound);
  }

  goToSlide(index: number, resetAutoplay = true): void {
    if (this.slides.length === 0) return;

    const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;

    if (nextIndex === this.currentIndex) return;

    const previousIndex = this.currentIndex;
    this.currentIndex = nextIndex;

    if (this.slideChangeTl) {
      this.slideChangeTl.kill();
      this.slideChangeTl = null;
    }

    const previousSlide = this.slides[previousIndex];
    const nextSlide = this.slides[nextIndex];

    this.slideChangeTl = gsap.timeline();
    this.slideChangeTl.add(() => {
      this.navButtons.forEach((button, index) => {
        const isActive = index === this.currentIndex;
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }

        const dot = this.navDots[index];
        if (!dot) return;

        dot.classList.toggle('bg-charcoal', isActive);
        dot.classList.toggle('bg-charcoal-60-opacity', !isActive);
      });
    }, 0);
    this.slideChangeTl.to(
      previousSlide,
      {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.out',
      },
      0,
    );
    this.slideChangeTl.to(
      nextSlide,
      {
        autoAlpha: 1,
        duration: 0.3,
        ease: 'power2.out',
      },
      0.4,
    );

    if (resetAutoplay) {
      this.startAutoplay();
    }
  }

  goToNextSlide(resetAutoplay = true): void {
    this.goToSlide(this.currentIndex + 1, resetAutoplay);
  }

  goToPrevSlide(resetAutoplay = true): void {
    this.goToSlide(this.currentIndex - 1, resetAutoplay);
  }

  startAutoplay(): void {
    if (this.slides.length <= 1) return;

    this.stopAutoplay();
    this.autoplayTimerId = setTimeout(this.onAutoplayTickBound, AUTOPLAY_DELAY);
  }

  stopAutoplay(): void {
    if (this.autoplayTimerId !== null) {
      clearTimeout(this.autoplayTimerId);
      this.autoplayTimerId = null;
    }
  }

  onAutoplayTick(): void {
    this.autoplayTimerId = null;
    this.goToNextSlide(false);
    this.startAutoplay();
  }

  onNavClick(event: Event): void {
    const button = (event.currentTarget as HTMLButtonElement | null) ?? null;
    if (!button) return;

    const index = Number.parseInt(button.dataset.index ?? '', 10);
    if (Number.isNaN(index)) return;

    this.goToSlide(index);
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 0) return;

    this.touchStartX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null) return;
    if (event.changedTouches.length === 0) return;

    const touchEndX = event.changedTouches[0].clientX;
    const deltaX = touchEndX - this.touchStartX;
    this.touchStartX = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    if (deltaX < 0) {
      this.goToNextSlide();
    } else {
      this.goToPrevSlide();
    }
  }

  onResize(): void {}

  disconnectedCallback(): void {
    this.stopAutoplay();
    this.removeEventListeners();

    if (this.slideChangeTl) {
      this.slideChangeTl.kill();
      this.slideChangeTl = null;
    }
  }
}

customElements.define('ugc-slider', UgcSlider);
